<template>
  <div>
    <!-- ⚠️ แท็บนี้ต่อ backend จริงเสมอ ไม่มีโหมดจำลอง — ถ้าต่อไม่ติดต้องบอกตรง ๆ
         ไม่ใช่แสร้งว่าบันทึกได้ (เหตุผลเต็มอยู่ในหัวไฟล์ qaRecommendApi.js) -->
    <v-alert v-if="loadError" type="error" variant="tonal" density="compact" class="mb-3">
      <span class="text-body-2">{{ $t('qaRecLoadError', { msg: loadError }) }}</span>
    </v-alert>

    <div v-if="loading" class="d-flex align-center py-6">
      <v-progress-circular indeterminate size="22" width="2" color="primary" class="mr-3" />
      <span class="text-body-2 text-medium-emphasis">{{ $t('qaRecLoading') }}</span>
    </div>

    <v-row v-else dense>
      <v-col cols="12">
        <v-alert type="info" variant="tonal" density="compact" class="mb-3" icon="mdi-database-check-outline">
          <span class="text-body-2">
            {{ $t('qaRecDbNote') }}
          </span>
        </v-alert>
      </v-col>

      <!-- ── Prompt ─────────────────────────────────────────────────────────── -->
      <v-col cols="12" md="7">
        <v-card class="qa-card-sm mb-3" variant="outlined">
          <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaRecPromptTitle') }}</span></v-card-title>
          <v-divider />
          <div class="pa-4">
            <v-textarea
              v-model="config.systemPrompt"
              variant="outlined"
              rows="14"
              hide-details
              auto-grow
              spellcheck="false"
              :placeholder="$t('qaEvalPromptPlaceholder')"
            />
            <v-alert type="warning" variant="tonal" density="compact" class="mt-2">
              <span class="text-body-2">
                {{ $t('qaRecPromptAppendNote') }}
              </span>
            </v-alert>
            <v-alert type="info" variant="tonal" density="compact" class="mt-2">
              <span class="text-body-2">
                {{ $t('qaRecLangNote') }}
              </span>
            </v-alert>
            <div class="text-caption text-medium-emphasis mt-2">
              {{ $t('qaRecPromptEmptyNote') }}
              <span v-if="promptTooLong" class="text-error">{{ $t('qaRecPromptTooLong', { n: promptLength }) }}</span>
            </div>

            <v-expansion-panels variant="accordion" class="mt-3">
              <v-expansion-panel>
                <v-expansion-panel-title class="text-body-2">{{ $t('qaRecJsonShapeTitle') }}</v-expansion-panel-title>
                <v-expansion-panel-text>
                  <pre class="qa-json">{{ jsonShape }}</pre>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <!-- ── การทำงาน ─────────────────────────────────────────────────────── -->
        <v-card class="qa-card-sm mb-3" variant="outlined">
          <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaEvalOperation') }}</span></v-card-title>
          <v-divider />
          <div class="pa-4">
            <div class="qa-set-row">
              <div>
                <div class="text-body-2 font-weight-medium">{{ $t('qaRecEnable') }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ $t('qaRecEnableDesc') }}
                </div>
              </div>
              <v-switch v-model="config.isEnabled" color="primary" density="compact" hide-details inset />
            </div>

            <div class="qa-set-row qa-set-row--stack">
              <div>
                <div class="text-body-2 font-weight-medium">{{ $t('qaRecModel') }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ $t('qaRecModelDesc') }}
                </div>
              </div>
              <v-autocomplete
                v-model="config.modelId"
                :items="modelItems"
                item-title="title"
                item-value="value"
                :loading="modelsLoading"
                :error-messages="modelsError"
                density="compact"
                variant="outlined"
                hide-details="auto"
                clearable
                auto-select-first
                prepend-inner-icon="mdi-robot"
                :placeholder="$t('qaRecModelPlaceholder')"
              >
                <template #item="{ props: itemProps, item }">
                  <v-list-item v-bind="itemProps" :subtitle="item.raw.subtitle" />
                </template>
              </v-autocomplete>
            </div>


          </div>
        </v-card>

        <!-- ── จังหวะการทำงาน (เหมือนเดิม) ──────────────────────────────────── -->
        <v-card class="qa-card-sm mb-3" variant="outlined">
          <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaRecCadenceTitle') }}</span></v-card-title>
          <v-divider />
          <div class="pa-4">
            <div class="qa-set-row">
              <div>
                <div class="text-body-2 font-weight-medium">{{ $t('qaRecEveryTurns') }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ $t('qaRecEveryTurnsDesc') }}
                </div>
              </div>
              <v-text-field
                v-model.number="config.everyNTurns"
                type="number"
                min="1"
                max="100"
                density="compact"
                variant="outlined"
                hide-details
                style="max-width: 100px"
              />
            </div>

          </div>
        </v-card>

        <!-- ── จังหวะการทำงาน (สายโทรศัพท์) ─────────────────────────────────── -->
        <v-card class="qa-card-sm mb-3" variant="outlined">
          <v-card-title class="py-3">
            <span class="text-subtitle-1">{{ $t('qaRecCallTitle') }}</span>
          </v-card-title>
          <v-divider />
          <div class="pa-4">
            <div class="qa-set-row">
              <div>
                <div class="text-body-2 font-weight-medium">{{ $t('qaRecCallEnable') }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ $t('qaRecCallEnableDesc') }}
                </div>
              </div>
              <v-switch v-model="config.callEnabled" color="primary" density="compact" hide-details inset />
            </div>

            <div class="qa-set-row qa-set-row--stack mt-3">
              <div>
                <div class="text-body-2 font-weight-medium">{{ $t('qaRecCallEverySeconds') }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ $t('qaRecCallEveryDesc') }}
                  <span v-if="callEveryMinutesText">{{ $t('qaRecCallEqualsMinutes', { text: callEveryMinutesText }) }}</span>
                </div>
              </div>
              <v-text-field
                :model-value="config.callEverySeconds"
                type="text"
                inputmode="numeric"
                density="compact"
                variant="outlined"
                :suffix="$t('qaRecSecondsSuffix')"
                style="max-width: 190px"
                :error-messages="callSecondsError"
                @keypress="onlyDigits"
                @paste="onPasteDigits"
                @update:model-value="onCallSecondsInput"
              />
            </div>
            <div class="text-caption text-medium-emphasis mt-1">
              {{ $t('qaRecSecondsRange', { min: minCallSeconds, max: maxCallSeconds }) }}
            </div>

            <v-alert type="warning" variant="tonal" density="compact" class="mt-3">
              <span class="text-body-2">
                {{ $t('qaRecCostWarn') }}
              </span>
            </v-alert>
            <v-alert type="info" variant="tonal" density="compact" class="mt-2">
              <span class="text-body-2">
                {{ $t('qaRecMiddlewareNote') }}
              </span>
            </v-alert>
          </div>
        </v-card>

      </v-col>

      <v-col cols="12" class="d-flex align-center flex-wrap ga-3">
        <v-btn
          color="primary"
          variant="flat"
          :loading="saving"
          :disabled="!!saveBlockedReason"
          prepend-icon="mdi-content-save-outline"
          @click="save"
        >
          {{ $t('qaSetSaveConfig') }}
        </v-btn>
        <!-- ⚠️ ปุ่มที่กดไม่ได้ต้องบอกเหตุผลเสมอ · เดิม callSecondsError บล็อกการบันทึก
             อยู่ใน save() แต่ไม่ได้อยู่ในเงื่อนไข disabled เลย = กดได้แต่ไม่เกิดอะไร -->
        <span v-if="saveBlockedReason" class="text-caption text-error">{{ saveBlockedReason }}</span>
      </v-col>
    </v-row>
  </div>
</template>

<script>
/**
 * QA Settings → แท็บ "คำแนะนำ AI"
 *
 * ⚠️ ต่อ backend จริงเสมอ — ค่าที่บันทึกที่นี่ไปสั่งงานเบื้องหลังที่เรียก LLM จริง
 *    และมีค่าใช้จ่ายต่อรอบ · ห้ามเติมทางสำรองที่เขียนลง localStorage แล้วขึ้นว่าบันทึกสำเร็จ
 *    (Q10 ลบชั้นจำลองทั้งชั้นแล้ว — ต่อ backend ไม่ติดต้องขึ้น error ตรง ๆ ไม่ใช่แสร้งว่าบันทึกได้)
 *
 * ⚠️ หน้านี้แสดงเฉพาะค่าที่คนตั้งจริง ๆ · ค่าที่เหลือ (เพดานรอบ/คิว, timeout,
 *    ขอบเขต window) **ไม่ได้อยู่ในค่าตั้งชุดนี้แล้ว** — ย้ายไป QaRecommendOptions
 *    (appsettings/โค้ด) ที่เดียว · ค่าเก่าที่เคยบันทึกไว้ถูกข้ามตอนอ่านและหายถาวร
 *    ตอนบันทึกทับ — อย่ากลับมาเขียนว่า "ค่าที่ไม่ได้แสดงจะไม่หาย" (เคยเขียนแล้วไม่จริง)
 *
 * ⚠️ ค่าตั้งมี **ชุดเดียว** ทับของเดิมทุกครั้งที่กดบันทึก (เลิกเก็บเป็น v1/v2/v3 แล้ว)
 *    หน้าจอจึงไม่มีเลขเวอร์ชันให้อ้างอีก — อย่าเอากลับมาโชว์
 *
 * ⚠️ รายการโมเดลเป็น "บอทจากหน้า Model Creator" (endpoint เดิมของ framework
 *    `ManageGPTModel/DDGPTModel_IsPublishByUser` ตัวเดียวกับแท็บ Next best action ใช้)
 *    ห้ามสร้างรายการโมเดลชุดที่สองขึ้นมาเอง ไม่งั้นจะมีที่ตั้งค่าโมเดลสองที่ที่ไม่ตรงกัน
 *
 * ⚠️ เก็บเป็น **ModelID ของบอท** ไม่ใช่ GroupModelID — การเลือกกลุ่มแล้วให้ระบบหยิบบอท
 *    ที่ publish ตัวแรกในกลุ่ม แปลว่าถ้ากลุ่มมีหลายบอท ผลจะเปลี่ยนไปเองตามลำดับที่คนอื่น
 *    เผลอ publish ทีหลัง โดยไม่มีใครแตะหน้านี้เลย
 *
 * แผนงาน: docs/qa-console/Plan-QA-Recomend.md §8
 */
import { getRecommendSetting, saveRecommendSetting } from '@/services/qaConsole';

/**
 * ⚠️ ต้องตรงกับ QaRecommendPromptBuilder.JsonShape ฝั่งหลังบ้านเสมอ
 *    (BAL/BAL/AppBAL/Social/QaRecommendPromptBuilder.cs) — หน้านี้ไม่ได้ดึงค่าจาก API
 *    ถ้าหลังบ้านเปลี่ยนรูปแล้วลืมแก้ที่นี่ คนอ่านหน้าจอจะเขียน prompt ตามรูปที่ผิด
 *
 * ⚠️ ช่อง "สิ่งที่ควรทำ" ใช้คีย์ agent ห้ามใช้ action — action ถูกจองไว้เป็นชื่อสำรอง
 *    ของ text มาก่อนแล้ว
 */
const JSON_SHAPE = `{
  "recommendations": [
    {
      "agent":  "สิ่งที่เจ้าหน้าที่ควรทำ (ไทย สั้น)",
      "text":   "ประโยคที่ส่งให้ลูกค้าได้ทันที (ภาษาเดียวกับลูกค้า)",
      "reason": "เหตุผลที่ควรตอบแบบนี้ (ไทย สั้น)"
    }
  ]
}`;

/**
 * ตัวเลือกตั้งต้น — ปล่อยให้ระบบใช้โมเดลของ OA ที่เคสนั้นสังกัด
 * (พฤติกรรมเดิมของ social pipeline ทั้งเส้น · ดู ISocialLlmBAL)
 */

/**
 * ขอบเขตของ "แนะนำทุก ๆ กี่วินาที" ระหว่างสาย
 *
 * ⚠️ ต้องตรงกับ QaRecommendSetting.Min/MaxCallEverySeconds ฝั่ง C# —
 *    ฝั่งนั้นเป็นคนปฏิเสธจริง ที่นี่แค่บอกก่อนกดเพื่อไม่ให้เสียเที่ยว
 */
const DEFAULT_CALL_SECONDS = 60;
const MIN_CALL_SECONDS = 10;
const MAX_CALL_SECONDS = 3600;

export default {
  name: 'QaRecommendSettings',
  emits: ['saved'],
  data() {
    return {
      config: {},
      loading: true,
      loadError: '',
      saving: false,
      modelsLoading: false,
      modelsError: '',
      modelItems: [],
      jsonShape: JSON_SHAPE,
    };
  },
  computed: {
    promptLength() {
      return (this.config.systemPrompt || '').length;
    },
    promptTooLong() {
      return this.promptLength > 8000;
    },
    /**
     * เหตุผลที่กดบันทึกไม่ได้ · '' = บันทึกได้
     *
     * ⚠️ **จุดเดียว**ที่รวมเงื่อนไขทั้งหมด — ปุ่มใช้ตัวนี้ disable และหน้าจอใช้ตัวนี้
     *    ขึ้นข้อความ · ถ้าแยกกันเมื่อไหร่ จะกลับไปเป็นเคส "ปุ่มกดได้แต่ save() return
     *    ทิ้งเงียบ ๆ" อีก
     */
    saveBlockedReason() {
      if (this.promptTooLong) return this.$t('qaRecPromptTooLongBlock', { n: this.promptLength });
      if (this.callSecondsError) return this.$t('qaRecCallSecondsBlock', { msg: this.callSecondsError });
      return '';
    },

    /** ข้อความ error ของช่องวินาที — ว่าง = ผ่าน (ปุ่มบันทึกใช้ค่านี้ตัดสินด้วย) */
    callSecondsError() {
      const v = this.config.callEverySeconds;
      if (v === '' || v === null || v === undefined) return this.$t('qaRecNeedSeconds');
      if (!Number.isInteger(v)) return this.$t('qaRecNeedInteger');
      if (v < MIN_CALL_SECONDS) return this.$t('qaRecMinSeconds', { n: MIN_CALL_SECONDS });
      if (v > MAX_CALL_SECONDS) return this.$t('qaRecMaxSeconds', { n: MAX_CALL_SECONDS });
      return '';
    },
    /** แปลงให้อ่านง่ายใต้ช่องกรอก — คนคิดเป็นนาที แต่ค่าที่เก็บเป็นวินาที */
    callEveryMinutesText() {
      const v = this.config.callEverySeconds;
      if (!Number.isInteger(v) || v < 60) return '';
      const minutes = v / 60;
      return this.$t('qaRecMinutes', { n: Number.isInteger(minutes) ? minutes : minutes.toFixed(1) });
    },
    minCallSeconds: () => MIN_CALL_SECONDS,
    maxCallSeconds: () => MAX_CALL_SECONDS,
  },
  mounted() {
    this.load();
    this.loadModels();
  },
  methods: {
    /** หัวรายการ dropdown เลือกโมเดล — เมธอด ไม่ใช่ const ระดับโมดูล (ดู QaEvaluatorSettings) */
    defaultModelItem() {
      return { title: this.$t('qaRecModelDefaultTitle'), subtitle: this.$t('qaEvalModelDefaultSub'), value: null };
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const value = await getRecommendSetting();

        // ⚠️ **ห้ามยัด object จาก server เข้า config ดิบ ๆ** — ฟิลด์ที่ backend รุ่นเก่า
        //    ยังไม่ส่งมาจะเป็น undefined แล้ว validation ฝั่งนี้จะฟ้องว่า "ต้องกรอกเวลา"
        //    ทั้งที่คนใช้ไม่เคยเห็นช่องนั้นว่างเลย · อาการคือกดบันทึกแล้วเงียบ
        //    (เจอตอนเทสจริง 7 ก.ย. 2026 — หน้าเว็บใหม่ชี้ backend ที่ยังไม่ได้ deploy)
        //
        //    ค่าตั้งต้นต้องตรงกับฝั่ง C# (QaRecommendSetting.DefaultCallEverySeconds)
        this.config = {
          ...value,
          callEnabled: value?.callEnabled ?? false,
          callEverySeconds: Number.isInteger(value?.callEverySeconds)
            ? value.callEverySeconds
            : DEFAULT_CALL_SECONDS,
        };
      } catch (err) {
        this.loadError = err.message;
      } finally {
        this.loading = false;
      }
    },

    /**
     * รายการบอทจากหน้า Model Creator — endpoint เดียวกับที่หน้าอื่นของระบบใช้
     * (`api/v0/ManageGPTModel/DDGPTModel_IsPublishByUser` · กรอง ActiveFlag + IsPublish
     *  และ scope ตาม role/company ให้แล้วฝั่ง server)
     *
     * ⚠️ อ่านรายการไม่ได้ต้องไม่ทำให้ตั้งค่าอื่นไม่ได้ — เหลือตัวเลือกตั้งต้นไว้แล้วบอกสาเหตุ
     */
    loadModels() {
      const self = this;
      if (!this.$call || typeof this.$call.DDGPTModel_IsPublishByUser !== 'function') return;

      this.modelsLoading = true;
      this.$call.DDGPTModel_IsPublishByUser(
        (dataResponse) => {
          self.modelsLoading = false;
          // response ห่อด้วย ResponseModel เหมือนทุก endpoint ของ framework (status 0 = สำเร็จ)
          // payload เป็น ResponseDropDownModel { id = ModelID, name = ModelName, TypeName = TypeLLM }
          if (!dataResponse || dataResponse.status !== 0) {
            self.modelsError = self.$t('qaEvalModelsFailed');
            return;
          }
          const rows = dataResponse.data || [];
          self.modelsError = rows.length ? '' : self.$t('qaEvalNoModels');
          self.modelItems = [self.defaultModelItem()].concat(
            rows.map((r) => ({
              title: r.name || `#${r.id}`,
              subtitle: [r.TypeName, `ModelID ${r.id}`].filter(Boolean).join(' · '),
              value: r.id,
            }))
          );
        },
        () => {
          self.modelsLoading = false;
          self.modelsError = self.$t('qaEvalModelsFailed');
        }
      );
    },


    /**
     * ช่องวินาที: ตัวเลขล้วนเท่านั้น
     *
     * ⚠️ ห้ามใช้ type="number" — เบราว์เซอร์ยอมให้พิมพ์ e / E / + / - / . ในช่องนั้น
     *    แล้วคืนค่าเป็น "" ตอนอ่าน ทำให้แยกไม่ออกว่า "ยังไม่ได้กรอก" กับ "กรอกของแปลก"
     */
    onlyDigits(event) {
      if (!/^[0-9]$/.test(event.key)) event.preventDefault();
    },
    onPasteDigits(event) {
      const text = (event.clipboardData || window.clipboardData)?.getData('text') ?? '';
      if (!/^[0-9]+$/.test(text.trim())) event.preventDefault();
    },
    onCallSecondsInput(value) {
      const digits = String(value ?? '').replace(/[^0-9]/g, '');
      this.config.callEverySeconds = digits === '' ? '' : Number(digits);
    },

    async save() {
      if (this.saveBlockedReason) {
        this.$emit('saved', { ok: false, message: this.saveBlockedReason });
        return;
      }

      this.saving = true;
      try {
        await saveRecommendSetting(this.config);
        this.$emit('saved', {
          ok: true,
          message: this.$t('qaRecSaved'),
        });
        await this.load();
      } catch (err) {
        this.$emit('saved', { ok: false, message: err.message });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* ⚠️ คัดลอกมาจาก QaNextBestActionSettings.vue โดยตั้งใจ — คลาสนี้เป็น scoped style
   ของแต่ละ component ไม่ใช่ของกลาง ถ้าไม่ใส่ แถวตั้งค่าจะกองซ้อนกันเป็นแนวตั้ง */
.qa-set-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
}
.qa-set-row:last-of-type {
  border-bottom: none;
}
.qa-set-row > div:first-child {
  flex: 1;
  min-width: 0;
}
/* แถวที่คำอธิบายยาว — วางช่องเลือกไว้ใต้คำอธิบายแทนที่จะบีบให้อยู่ข้าง ๆ */
.qa-set-row--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.qa-json {
  margin: 0;
  padding: 12px;
  background: #f6f8fa;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.5;
  overflow-x: auto;
}
</style>
